/*
 * c_smooth-scroll.js
 * ページ全体に軽めの慣性スクロール（Lenis）を付与する。
 * - PC のみ有効（タッチ端末・SP レイアウトは端末ネイティブの慣性に任せる）
 * - GSAP ScrollTrigger（c_business のピン留め）と同期
 * - 既存のプログラム的スムーススクロール（window.scrollTo / scrollIntoView）を Lenis 経由に通す
 * - naiveDimensions でページ高さの後伸び（画像 lazy 等）でも limit をライブ参照
 * Lenis は既存の GSAP と同様に CDN から読み込む。
 */
(function () {
  'use strict';

  const LENIS_SRC = 'https://cdn.jsdelivr.net/npm/lenis@1.1.14/dist/lenis.min.js';
  const LERP = 0.11; // 軽め・すぐ止まる（実機で微調整する初期値）

  // タッチ端末（スマホ・タブレット）と SP レイアウト幅はネイティブ慣性に任せる
  const isTouchOrSp =
    window.matchMedia('(max-width: 980px)').matches ||
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  if (isTouchOrSp) return;

  /*
   * Lenis は wheel を preventDefault して自前で慣性を描くため、放っておくとモーダルや
   * ドロワーなど「自前でスクロールする領域」まで巻き添えで動かなくなる。
   * 要素ごとに data-lenis-prevent を付ける方法もあるが、付け忘れると黙って壊れるので、
   * 実際に縦スクロールできる要素かどうかをここで自動判定して一括で除外する。
   * （data-lenis-prevent も Lenis 側で引き続き有効。自動判定が効かない場合の逃げ道として使える）
   */
  function isNestedScrollArea(node) {
    if (!node || node.nodeType !== 1) return false;
    // body/html はページ本体。overflow-x: hidden 等で overflow-y が auto に計算される
    // ことがあり、誤爆すると慣性スクロールが全ページで無効になるため必ず除外する
    if (node === document.body || node === document.documentElement) return false;
    // 大半の要素はここで抜ける（レイアウト読みを1回で済ませる）
    if (node.scrollHeight <= node.clientHeight) return false;
    const overflowY = window.getComputedStyle(node).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  }

  function init() {
    if (typeof window.Lenis === 'undefined') return;

    const lenis = new Lenis({
      lerp: LERP,
      smoothWheel: true,
      wheelMultiplier: 1,
      prevent: isNestedScrollArea,
      // resize() は animatedScroll を actualScroll に戻して慣性を殺す。
      // 画像ロードで高さが伸びても limit をライブ参照し、resize 不要にする。
      autoResize: false,
      __experimental__naiveDimensions: true
    });
    window.lenis = lenis;

    /*
     * OneTrust など「body に overflow:hidden を付けて背面をロックする」外部/テーマ側の
     * スクロールロックへの追従。Lenis は wheel を preventDefault して自前で慣性を描くため、
     * ネイティブの body{overflow:hidden} だけでは背面スクロールが止まらない（OneTrust の
     * Cookie 同意オーバーレイ等が背面ごと動いてしまう）。body の overflow を監視し、ロック中は
     * stop()・解除で start() して同期する。
     * - overflow-x: hidden 等での誤爆を避けるため overflowY を見る（縦ロックのみ拾う）
     * - Lenis はスクロール中 <html> のクラスのみ操作し <body> は触らないため、この監視が
     *   Lenis 自身の動作で毎フレーム再発火することはない（body 属性変化時のみ発火）
     * - 履歴ページ（c_history-page）は position:fixed でロックし overflow を変えないため独立
     */
    let lockedByOverflow = false;
    function syncScrollLock() {
      const locked = window.getComputedStyle(document.body).overflowY === 'hidden';
      if (locked === lockedByOverflow) return;
      lockedByOverflow = locked;
      if (locked) lenis.stop();
      else lenis.start();
    }
    const bodyLockObserver = new MutationObserver(syncScrollLock);
    bodyLockObserver.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    syncScrollLock(); // Lenis 起動前に既にロックされていた場合の初期同期

    // rAF ループ。GSAP が現れたら gsap.ticker に一本化し二重 rAF を避ける。
    let drivenByGsap = false;
    function raf(time) {
      lenis.raf(time);
      if (!drivenByGsap) requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // GSAP ScrollTrigger 連携。ScrollTrigger は c_business で遅延ロードされるため、
    // 出現を監視してからピン留めのスクロール同期を配線する。
    function wireScrollTrigger() {
      if (typeof window.ScrollTrigger === 'undefined') return false;
      lenis.on('scroll', window.ScrollTrigger.update);
      if (typeof window.gsap !== 'undefined' && window.gsap.ticker) {
        drivenByGsap = true;
        window.gsap.ticker.add(function (t) {
          lenis.raf(t * 1000);
        });
        window.gsap.ticker.lagSmoothing(0);
      }
      return true;
    }
    if (!wireScrollTrigger()) {
      const poll = window.setInterval(function () {
        if (wireScrollTrigger()) window.clearInterval(poll);
      }, 300);
      // ScrollTrigger を使わないページ用に一定時間で監視を打ち切る
      window.setTimeout(function () {
        window.clearInterval(poll);
      }, 15000);
    }

    // 既存のプログラム的スムーススクロールを Lenis 経由に通す（競合防止）。
    // window.scrollTo({ top, behavior: 'smooth' }) … アンカーナビ・サステナ各ページ等
    const nativeScrollTo = window.scrollTo.bind(window);
    window.scrollTo = function (arg) {
      if (arg && typeof arg === 'object' && arg.behavior === 'smooth' && typeof arg.top === 'number') {
        lenis.scrollTo(arg.top);
        return;
      }
      return nativeScrollTo.apply(window, arguments);
    };

    // Element.scrollIntoView({ behavior: 'smooth' }) … 履歴ページ等のページ内ジャンプ。
    // scroll-margin-top を尊重して Lenis で移動する。
    const nativeScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function (arg) {
      if (arg && typeof arg === 'object' && arg.behavior === 'smooth') {
        const marginTop = parseFloat(window.getComputedStyle(this).scrollMarginTop) || 0;
        lenis.scrollTo(this, { offset: -marginTop });
        return;
      }
      return nativeScrollIntoView.apply(this, arguments);
    };

    /*
     * 画像遅延読み込みでページ高さが後から伸びても、naiveDimensions により
     * limit は常に documentElement.scrollHeight 基準で算出される。
     * （従来の lenis.resize() は慣性スクロールを actualScroll にスナップして
     * 引っかかりの原因になるため、スクロール中は呼ばない）
     *
     * ScrollTrigger のピン留め等だけ、初期レイアウト確定後に refresh する。
     */
    function refreshScrollTrigger() {
      if (typeof window.ScrollTrigger !== 'undefined') {
        window.ScrollTrigger.refresh();
      }
    }

    window.addEventListener('load', refreshScrollTrigger);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refreshScrollTrigger).catch(function () {});
    }
  }

  function load() {
    if (typeof window.Lenis !== 'undefined') {
      init();
      return;
    }
    const s = document.createElement('script');
    s.src = LENIS_SRC;
    s.onload = init;
    document.head.appendChild(s);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', load);
  } else {
    load();
  }
})();
